import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { BehaviorSubject, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIG
import { trackById } from 'v2/modules/shared/util/track-by.util';

import { ILocationInventory } from './../../../location/interfaces/location.interface';
import { SnackBarConfig } from './../../../shared/configs/snackbar.config';

// INTERFACES
import { AppIntegrationAccountProductsApiService } from './../../../shared/services/api/app-integration/app-integration-account-products-api.service';
import { ChannelProductMappingApiService } from './../../../shared/services/api/channel/channel-product-mapping-api.service';
import { EProductMappingType } from './../../interfaces/channel-product-mapping.interface';

// SERVICES

// UTILS

interface ICellRendererParamsChannel extends ICellEditorParams {
  mappingType :EProductMappingType;
}

@Component({
  selector: 'lum-channel-product-mapping-search-inv-location',
  templateUrl: './channel-product-mapping-search-inv-location.component.html',
  styleUrls: ['./channel-product-mapping-search-inv-location.component.scss'],
})
export class ChannelProductMappingSearchInvLocationComponent
  implements ICellEditorAngularComp, OnInit, OnDestroy
{
  inputControl = new FormControl();
  locations :ILocationInventory[] = [];
  selectedItem :Partial<ILocationInventory> | null = null;

  private params :ICellRendererParamsChannel;
  private onDestroy$ = new Subject();

  private readonly _loading = new BehaviorSubject<boolean>(<boolean>false);
  loading$ = this._loading.asObservable();

  trackById = trackById;

  constructor(
    private channelProductMappingApiService :ChannelProductMappingApiService,
    private appIntegrationAccountProductsApiService :AppIntegrationAccountProductsApiService,
    private snackbar :MatSnackBar,
  ) {}

  ngOnInit() {
    this.fetchOptions();
  }

  agInit(params :ICellRendererParamsChannel) :void {
    this.params = params;
  }

  getValue() :any {
    return this.inputControl?.value;
  }

  afterGuiAttached() :void {}

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  fetchOptions() :void {
    let url :string = '';
    switch (this.params.mappingType) {
      case EProductMappingType.CHANNEL:
        url = `channels/${this.params.data?.channelId}/inventory-locations`;
        break;

      case EProductMappingType.APP_INTEGRATION_ACCOUNT:
        url = `app-integration-accounts/${this.params.data?.appIntegrationAccountId}/inventory-locations`;
        break;
    }

    this._loading.next(true);
    this.channelProductMappingApiService
      .getInventoryLocationsByChannel({ url })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (locations) => {
          if (!locations.length) {
            this.snackbar.open('No locations found.', 'Dismiss', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          }

          this.locations = locations;
          this._loading.next(false);
        },
        error: (error) => {
          this._loading.next(false);
          this.snackbar.open('Failed to fetch locations', 'Dismiss', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  onSelectValue(item :ILocationInventory) :void {
    this.selectedItem = item;
    const payload = {
      inventoryLocationId: (this.selectedItem?.id || 0).toString(),
    };

    switch (this.params.mappingType) {
      case EProductMappingType.CHANNEL:
        payload['channelId'] = this.params.data?.channelId;

        this.channelProductMappingApiService
          .saveChannelProductOverride(this.params.data.id, payload)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.params.stopEditing();
              this.params.api.stopEditing();
            }),
          )
          .subscribe({
            next: (response :any) => {
              this.snackbar.open('Successfully mapped location.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              // Refresh the grid
              this.params.api.refreshCells();
            },
            error: (error :any) => {
              // rollback
              this.selectedItem = null;

              this.snackbar.open('Failed to map location.', 'Error', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            },
          });
        break;

      case EProductMappingType.APP_INTEGRATION_ACCOUNT:
        this.appIntegrationAccountProductsApiService
          .update(this.params.data.id, payload)
          .pipe(
            takeUntil(this.onDestroy$),
            finalize(() => {
              this.params.stopEditing();
              this.params.api.stopEditing();
            }),
          )
          .subscribe({
            next: (response :any) => {
              this.snackbar.open('Successfully mapped location.', 'Success', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
              // Refresh the grid
              this.params.api.refreshCells();
            },
            error: (error :any) => {
              // rollback
              this.selectedItem = null;

              this.snackbar.open('Failed to map location.', 'Error', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            },
          });
        break;

      default:
        break;
    }
  }

  removeSelectedItem() :void {
    this.selectedItem = null;
  }

  displaySelectedItem(item :any) :string {
    return item?.name || '';
  }
}
